// src/pages/ventes/Clients.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Pagination,
    Dropdown, Modal, Alert, Spinner, Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   CONFIRM MODAL
   ============================================================ */
const ConfirmModal = ({ show, onHide, onConfirm, title, message, variant = 'danger', loading }) => {
    if (!show) return null;
    const bg = variant === 'danger'
        ? `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
        : `linear-gradient(135deg, ${COLORS.warning}, #d97706)`;
    return (
        <Modal show={show} onHide={onHide} centered className="modal-charte">
            <Modal.Header closeButton className="modal-header-charte" style={{ background: bg }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="modal-header-icon"><i className="ri-alert-line"></i></div>
                    <div>
                        <Modal.Title className="fs-5 fw-bold text-white mb-0">{title}</Modal.Title>
                        <p className="text-white-50 mb-0 small">Confirmation requise</p>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-4 text-center">
                <p className="text-muted mb-0">{message}</p>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                <Button onClick={onHide} className="btn-modern-outline" disabled={loading}>Annuler</Button>
                <Button onClick={onConfirm} className="btn-modern-danger" disabled={loading}>
                    {loading ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><i className="ri-delete-bin-line me-2"></i>Confirmer</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Clients() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('raison_sociale');
    const [sortDirection, setSortDirection] = useState('asc');
    const [selectedClients, setSelectedClients] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [stats, setStats] = useState(null);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        fetchClients();
        fetchStats();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchClients = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php?action=statistiques`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const handleDeleteClick = (client) => {
        setClientToDelete(client);
        setShowConfirmDelete(true);
    };

    const handleDeleteConfirm = async () => {
        if (!clientToDelete) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php?id=${clientToDelete.id_client}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setShowConfirmDelete(false);
                fetchClients();
                fetchStats();
                showToast('success', 'Client supprimé', 'Le client a été supprimé avec succès');
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de la suppression");
        } finally {
            setDeleting(false);
            setClientToDelete(null);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatusConfig = (client) => {
        if (client.encours > 0) return {
            gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
            text: 'En cours',
            icon: 'ri-time-line'
        };
        if (client.total_achats > 0) return {
            gradient: 'linear-gradient(135deg, #10b981, #059669)',
            text: 'Actif',
            icon: 'ri-checkbox-circle-line'
        };
        return {
            gradient: 'linear-gradient(135deg, #64748b, #475569)',
            text: 'Inactif',
            icon: 'ri-pause-circle-line'
        };
    };

    const getSoldeClass = (solde) => {
        if (solde > 0) return 'text-danger';
        if (solde < 0) return 'text-success';
        return 'text-secondary';
    };

    /* ============================================================
       FILTER / SORT / PAGINATION
       ============================================================ */
    const filteredAndSortedItems = useMemo(() => {
        let filtered = data.filter(item =>
            item.raison_sociale?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.code_client?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.email?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.telephone?.includes(filterText)
        );
        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];
            if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
        });
        return filtered;
    }, [filterText, sortField, sortDirection, data]);

    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedClients(currentItems.map(c => c.id_client));
        else setSelectedClients([]);
    };

    const handleSelectClient = (clientId) => {
        setSelectedClients(prev =>
            prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
        );
    };

    const isAllSelected = currentItems.length > 0 && selectedClients.length === currentItems.length;
    const isSomeSelected = selectedClients.length > 0 && selectedClients.length < currentItems.length;

    /* ============================================================
       EXPORT
       ============================================================ */
    const handleExport = (format) => {
        setExportLoading(true);
        const dataToExport = selectedClients.length > 0
            ? data.filter(c => selectedClients.includes(c.id_client))
            : filteredAndSortedItems;

        if (!dataToExport.length) {
            showToast('warning', 'Export', 'Aucune donnée à exporter');
            setExportLoading(false);
            return;
        }

        if (format === "csv") {
            const headers = ["Code", "Raison sociale", "Téléphone", "Email", "Total achats", "Encours", "Plafond", "Statut"];
            const csvContent = [
                "\uFEFF" + headers.join(";"),
                ...dataToExport.map(c => [
                    c.code_client, c.raison_sociale, c.telephone || '', c.email || '',
                    c.total_achats || 0, c.encours || 0, c.plafond_credit || 0, getStatusConfig(c).text
                ].map(v => `"${v}"`).join(";"))
            ].join("\n");
            downloadFile(csvContent, "text/csv;charset=utf-8;", `export-clients-${Date.now()}.csv`);
            showToast('success', 'Export CSV', `${dataToExport.length} client(s) exporté(s)`);
        } else if (format === "excel") {
            const ws = XLSX.utils.json_to_sheet(dataToExport.map(c => ({
                Code: c.code_client,
                "Raison sociale": c.raison_sociale,
                Téléphone: c.telephone,
                Email: c.email,
                "Total achats": c.total_achats,
                Encours: c.encours,
                Plafond: c.plafond_credit,
                Statut: getStatusConfig(c).text
            })));
            ws['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Clients");
            XLSX.writeFile(wb, `export-clients-${Date.now()}.xlsx`);
            showToast('success', 'Export Excel', `${dataToExport.length} client(s) exporté(s)`);
        } else if (format === "pdf") {
            generatePDF(dataToExport);
            showToast('success', 'Export PDF', `${dataToExport.length} client(s) exporté(s)`);
        }
        setExportLoading(false);
    };

    const generatePDF = (data) => {
        const doc = new jsPDF();
        const primaryColor = COLORS.primary ? hexToRgb(COLORS.primary) : [41, 90, 167];
        const headerColor = [240, 240, 240];
        const alternateColor = [248, 249, 250];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, 210, 25, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("LISTE DES CLIENTS", 105, 15, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
        doc.text(`Total: ${data.length} client(s)`, 180, 22, { align: 'right' });

        const pageWidth = 210;
        const margin = 15;
        let yPosition = 35;
        const colWidths = [20, 45, 25, 25, 25, 20, 20];
        const headers = ["CODE", "RAISON SOCIALE", "TÉLÉPHONE", "EMAIL", "TOTAL ACHATS", "ENCOURS", "STATUT"];

        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        let xPosition = margin;
        headers.forEach((header, index) => {
            doc.text(header, xPosition + 2, yPosition);
            xPosition += colWidths[index];
        });
        yPosition += 6;

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        data.forEach((item, index) => {
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
                doc.setFillColor(...headerColor);
                doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(8);
                doc.setFont("helvetica", "bold");
                let newX = margin;
                headers.forEach((header, idx) => {
                    doc.text(header, newX + 2, yPosition);
                    newX += colWidths[idx];
                });
                yPosition += 6;
                doc.setFontSize(7);
                doc.setFont("helvetica", "normal");
            }
            if (index % 2 === 0) {
                doc.setFillColor(...alternateColor);
                doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
            }
            doc.setTextColor(0, 0, 0);
            const rowData = [
                item.code_client,
                item.raison_sociale.length > 20 ? item.raison_sociale.substring(0, 20) + '...' : item.raison_sociale,
                item.telephone || '-',
                item.email ? (item.email.length > 15 ? item.email.substring(0, 15) + '...' : item.email) : '-',
                formatCurrency(item.total_achats || 0),
                formatCurrency(item.encours || 0),
                getStatusConfig(item).text
            ];
            let currentX = margin;
            rowData.forEach((cell, cellIndex) => {
                if (cellIndex === 5) {
                    const encours = item.encours || 0;
                    doc.setTextColor(...(encours > 0 ? [220, 53, 69] : [40, 167, 69]));
                } else {
                    doc.setTextColor(0, 0, 0);
                }
                doc.text(cell, currentX + 1, yPosition);
                currentX += colWidths[cellIndex];
            });
            yPosition += 6;
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, 275, pageWidth - margin, 275);
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
            doc.text("Système de Gestion", margin, 282);
            doc.text(new Date().getFullYear().toString(), pageWidth - margin, 282, { align: 'right' });
        }
        doc.save(`clients-${Date.now()}.pdf`);
    };

    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [41, 90, 167];
    };

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

    /* ============================================================
       SORTABLE HEADER
       ============================================================ */
    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortField === field
                    ? <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    : <i className="ri-arrow-down-up-line" style={{ opacity: 0.35 }}></i>}
            </div>
        </th>
    );

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !data.length) {
         return (
                         <>
                             <Header />
                             <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                                 <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                     <div className="text-center" style={{ maxWidth: '400px' }}>
                                         <div className="loading-ring-wrapper mb-4">
                                             <div className="loading-ring"></div>
                                             <div className="loading-ring-inner"></div>
                                             <i className="ri-loader-4-fill loading-icon"></i>
                                         </div>
                                         <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                             Chargement de la page...
                                         </h5>
                                         <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                         <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                             <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                                 <div
                                                     className="progress-bar progress-bar-striped progress-bar-animated"
                                                     style={{
                                                         width: '100%',
                                                         background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                         backgroundSize: '200% 100%',
                                                         animation: 'shimmer 1.5s linear infinite'
                                                     }}
                                                 />
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                             <Footer />
                             <style>{`
                                 .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                                 .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                                 .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                                 .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                                 @keyframes spin { to { transform: rotate(360deg); } }
                                 @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                                 @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                             `}</style>
                         </>
                     );
    }

    if (error && !data.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchClients} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-user-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des clients</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-group-line"></i>
                                        {filteredAndSortedItems.length} client(s)
                                    </span>
                                    {selectedClients.length > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-checkbox-line"></i>
                                            {selectedClients.length} sélectionné(s)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchClients} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>

                            <Dropdown align="end">
                                <Dropdown.Toggle className="btn-modern-outline" disabled={exportLoading}>
                                    <i className="ri-download-line"></i> Exporter
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="dropdown-menu-charte">
                                    <Dropdown.Item onClick={() => handleExport('csv')}>
                                        <i className="ri-file-csv-line text-info me-2"></i> Export CSV
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleExport('excel')}>
                                        <i className="ri-file-excel-line text-success me-2"></i> Export Excel
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleExport('pdf')}>
                                        <i className="ri-file-pdf-line text-danger me-2"></i> Export PDF
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <Button className="btn-modern-primary" as={Link} to="/ventes/clients/ajouter">
                                <i className="ri-add-line"></i> Nouveau client
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total clients</span>
                                        <h3 className="kpi-value-charte">{formatNumber(stats.total_clients || 0)}</h3>
                                        <span className="kpi-unit-charte">{stats.nouveaux_mois || 0} nouveaux ce mois</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Clients débiteurs</span>
                                        <h3 className="kpi-value-charte">{formatNumber(stats.clients_debiteurs || 0)}</h3>
                                        <span className="kpi-unit-charte">Solde: {formatCurrency(stats.solde_moyen || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-bank-card-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Avec crédit</span>
                                        <h3 className="kpi-value-charte">{formatNumber(stats.clients_avec_credit || 0)}</h3>
                                        <span className="kpi-unit-charte">Plafond disponible</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Nouveaux</span>
                                        <h3 className="kpi-value-charte">{formatNumber(stats.nouveaux_jour || 0)}</h3>
                                        <span className="kpi-unit-charte">Aujourd'hui</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="6">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par nom, code, email, téléphone..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="d-flex gap-2">
                                        <Form.Select
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            className="filter-select-charte"
                                        >
                                            <option value={5}>5 lignes</option>
                                            <option value={10}>10 lignes</option>
                                            <option value={25}>25 lignes</option>
                                            <option value={50}>50 lignes</option>
                                        </Form.Select>
                                        {filterText && (
                                            <Button
                                                onClick={() => setFilterText('')}
                                                className="btn-modern-light-outline"
                                            >
                                                <i className="ri-close-line"></i> Effacer
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                                <Col md="2">
                                    <div className="text-end">
                                        <span className="count-badge-charte">
                                            <i className="ri-stack-line"></i>
                                            {filteredAndSortedItems.length} client(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-building-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des clients</span>
                                    <span className="badge-header-light">{filteredAndSortedItems.length}</span>
                                </div>
                                {selectedClients.length > 0 && (
                                    <span className="badge-header-light">
                                        <i className="ri-checkbox-multiple-line"></i> {selectedClients.length} sélectionné(s)
                                    </span>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4" style={{ width: '45px' }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input rounded-checkbox-charte"
                                                    checked={isAllSelected}
                                                    ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <SortableHeader field="code_client">Code</SortableHeader>
                                            <SortableHeader field="raison_sociale">Client</SortableHeader>
                                            <th>Contact</th>
                                            {/* <SortableHeader field="encours" className="text-end">Encours</SortableHeader>
                                            */}
                                           
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((client) => {
                                            const status = getStatusConfig(client);
                                            return (
                                                <tr key={client.id_client}>
                                                    <td className="ps-4">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input rounded-checkbox-charte"
                                                            checked={selectedClients.includes(client.id_client)}
                                                            onChange={() => handleSelectClient(client.id_client)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-hashtag"></i>{client.code_client}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{client.raison_sociale}</div>
                                                        {client.numero_compte && <small className="text-muted">{client.numero_compte}</small>}
                                                    </td>
                                                    <td>
                                                        {client.telephone && (
                                                            <div className="info-line-charte" style={{ marginBottom: '2px' }}>
                                                                <i className="ri-phone-line"></i>
                                                                <span>{client.telephone}</span>
                                                            </div>
                                                        )}
                                                        {client.email && (
                                                            <div className="info-line-charte">
                                                                <i className="ri-mail-line"></i>
                                                                <span>{client.email}</span>
                                                            </div>
                                                        )}
                                                        {!client.telephone && !client.email && <span className="text-muted">—</span>}
                                                    </td>
                                                    {/* <td className="text-end">
                                                        <span className={`fw-bold ${getSoldeClass(client.encours)}`}>
                                                            {formatCurrency(client.encours || 0)}
                                                        </span>
                                                        {client.encours > 0 && (
                                                            <small className="d-block text-muted">{client.nb_factures || 0} facture(s)</small>
                                                        )}
                                                    </td> */}
                                                    <td className="text-center">
                                                        <span className="status-badge-charte" style={{ background: status.gradient }}>
                                                            <i className={status.icon}></i>
                                                            {status.text}
                                                        </span>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/ventes/clients/${client.id_client}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                icon="ri-pencil-line"
                                                                onClick={() => navigate(`/ventes/clients/modifier/${client.id_client}`)}
                                                                tooltip="Modifier"
                                                                color={COLORS.info}
                                                            />
                                                            <ActionButton
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => handleDeleteClick(client)}
                                                                tooltip="Supprimer"
                                                                color={COLORS.danger}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun client trouvé</h6>
                                                        <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> client(s)
                                        {selectedClients.length > 0 && (
                                            <span className="ms-2" style={{ color: COLORS.primary }}>
                                                • {selectedClients.length} sélectionné(s)
                                            </span>
                                        )}
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;
                                            return (
                                                <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL DÉTAILS CLIENT ===== */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-eye-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Détails du client</Modal.Title>
                            <p className="text-white-50 mb-0 small">Informations complètes</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedClient && (
                        <>
                            <div className="summary-card-charte mb-4">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fw-bold mb-1" style={{ color: COLORS.primaryDark }}>{selectedClient.raison_sociale}</h5>
                                        <p className="text-muted small mb-0">
                                            Code: {selectedClient.code_client} • Inscrit le {formatDate(selectedClient.date_creation)}
                                        </p>
                                    </div>
                                    <span className="status-badge-charte" style={{ background: getStatusConfig(selectedClient).gradient }}>
                                        <i className={getStatusConfig(selectedClient).icon}></i>
                                        {getStatusConfig(selectedClient).text}
                                    </span>
                                </div>
                            </div>

                            <div className="section-title-charte">
                                <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-contacts-line"></i>
                                </span>
                                <h6>Coordonnées</h6>
                            </div>
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <div className="mini-info-box-charte">
                                        <span className="mini-info-label-charte">Téléphone</span>
                                        <span className="mini-info-value-charte">{selectedClient.telephone || '—'}</span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mini-info-box-charte">
                                        <span className="mini-info-label-charte">Email</span>
                                        <span className="mini-info-value-charte">{selectedClient.email || '—'}</span>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="mini-info-box-charte">
                                        <span className="mini-info-label-charte">Adresse</span>
                                        <span className="mini-info-value-charte">{selectedClient.adresse || '—'}</span>
                                    </div>
                                </Col>
                            </Row>

                            <div className="section-title-charte">
                                <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                    <i className="ri-briefcase-line"></i>
                                </span>
                                <h6>Identifiants juridiques</h6>
                            </div>
                            <Row className="g-2 mb-4">
                                {[
                                    { icon: 'ri-bank-card-line', label: 'Numéro compte', value: selectedClient.numero_compte },
                                    { icon: 'ri-fingerprint-line', label: 'ICE', value: selectedClient.ice },
                                    { icon: 'ri-file-list-line', label: 'Patente', value: selectedClient.patente },
                                    { icon: 'ri-book-2-line', label: 'RC', value: selectedClient.rc },
                                    { icon: 'ri-receipt-line', label: 'IF', value: selectedClient.identifiant_fiscal },
                                    { icon: 'ri-shield-user-line', label: 'CNSS', value: selectedClient.cnss }
                                ].map((item, index) => (
                                    <Col md={6} key={index}>
                                        <div className="info-row-charte">
                                            <span className="info-label-charte"><i className={item.icon}></i>{item.label}</span>
                                            <span className="info-value-charte">{item.value || '—'}</span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            <div className="section-title-charte">
                                <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                    <i className="ri-money-euro-circle-line"></i>
                                </span>
                                <h6>Informations financières</h6>
                            </div>
                            <Row className="g-3 mb-4">
                                <Col md={4}>
                                    <div className="stat-card-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-shopping-bag-3-line"></i>
                                        </div>
                                        <div className="stat-label-charte">Total achats</div>
                                        <div className="stat-value-charte" style={{ color: COLORS.primary }}>
                                            {formatCurrency(selectedClient.total_achats || 0)}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="stat-card-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                            <i className="ri-time-line"></i>
                                        </div>
                                        <div className="stat-label-charte">Encours</div>
                                        <div className={`stat-value-charte ${getSoldeClass(selectedClient.encours)}`}>
                                            {formatCurrency(selectedClient.encours || 0)}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="stat-card-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-shield-check-line"></i>
                                        </div>
                                        <div className="stat-label-charte">Plafond crédit</div>
                                        <div className="stat-value-charte" style={{ color: COLORS.info }}>
                                            {formatCurrency(selectedClient.plafond_credit || 0)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {selectedClient.factures?.length > 0 && (
                                <>
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-file-list-3-line"></i>
                                        </span>
                                        <h6>Dernières factures</h6>
                                    </div>
                                    <div className="table-responsive">
                                        <Table className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th>Facture</th>
                                                    <th>Date</th>
                                                    <th className="text-end">Montant</th>
                                                    <th className="text-end">Reste</th>
                                                    <th className="text-center">Statut</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedClient.factures.map(facture => {
                                                    const statut = facture.statut === 'payee'
                                                        ? { bg: `${COLORS.success}15`, color: COLORS.success, label: 'Payée' }
                                                        : facture.statut === 'payee_partielle'
                                                        ? { bg: `${COLORS.warning}15`, color: COLORS.warning, label: 'Partielle' }
                                                        : { bg: `${COLORS.danger}15`, color: COLORS.danger, label: 'Impayée' };
                                                    return (
                                                        <tr key={facture.id_facture}>
                                                            <td className="fw-semibold">{facture.numero_facture}</td>
                                                            <td className="text-muted">{formatDate(facture.date_facture)}</td>
                                                            <td className="text-end">{formatCurrency(facture.montant)}</td>
                                                            <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                                {formatCurrency(facture.reste)}
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge-status-charte" style={{ background: statut.bg, color: statut.color }}>
                                                                    {statut.label}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}

                            <div className="mt-4 pt-3 border-top text-muted small">
                                ID: #{selectedClient.id_client} • Créé le {formatDate(selectedClient.date_creation)}
                                {selectedClient.date_modification && ` • Modifié le ${formatDate(selectedClient.date_modification)}`}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowModal(false)} className="btn-modern-outline">Fermer</Button>
                    <Button
                        className="btn-modern-primary"
                        as={Link}
                        to={`/ventes/clients/modifier/${selectedClient?.id_client}`}
                    >
                        <i className="ri-pencil-line me-2"></i>Modifier
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL CONFIRMATION SUPPRESSION ===== */}
            <ConfirmModal
                show={showConfirmDelete}
                onHide={() => { setShowConfirmDelete(false); setClientToDelete(null); }}
                onConfirm={handleDeleteConfirm}
                title="Supprimer ce client ?"
                message={<>Êtes-vous sûr de vouloir supprimer <strong>{clientToDelete?.raison_sociale}</strong> ? Cette action est irréversible.</>}
                variant="danger"
                loading={deleting}
            />

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                    min-width: 120px;
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-modern-light-outline {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    border-radius: 50px;
                    padding: 0.5rem 1rem;
                    font-size: 0.85rem;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-light-outline:hover {
                    background: rgba(255,255,255,0.25);
                    color: white;
                }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .info-line-charte {
                    display: flex; align-items: center; gap: 6px;
                    font-size: 0.8rem; color: #475569;
                }
                .info-line-charte i { color: var(--secondary); font-size: 0.85rem; }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                }
                .rounded-checkbox-charte {
                    width: 18px; height: 18px;
                    border-radius: 6px !important;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 1.5px solid #cbd5e1;
                }
                .rounded-checkbox-charte:checked {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }
                .rounded-checkbox-charte:indeterminate {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover:not(:disabled) {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== SUMMARY CARD MODAL ===== */
                .summary-card-charte {
                    background: linear-gradient(135deg, #ffffff, #f8fafc);
                    padding: 1.25rem;
                    border-radius: 14px;
                    border: 1px solid #e2e8f0;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== MINI INFO BOX ===== */
                .mini-info-box-charte {
                    background: #f8fafc; border-radius: 10px;
                    padding: 0.75rem 1rem;
                    border: 1px solid #f1f5f9;
                }
                .mini-info-label-charte {
                    display: block; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.4px;
                    color: #94a3b8; font-weight: 600; margin-bottom: 4px;
                }
                .mini-info-value-charte {
                    font-size: 0.85rem; font-weight: 600; color: #1e293b;
                }

                /* ===== INFO ROW ===== */
                .info-row-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.6rem 0.85rem;
                    background: #f8fafc;
                    border-radius: 10px;
                    border: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .info-label-charte {
                    display: inline-flex; align-items: center; gap: 6px;
                    color: #64748b; font-size: 0.8rem;
                }
                .info-label-charte i { color: var(--secondary); }
                .info-value-charte { font-weight: 600; color: #1e293b; font-size: 0.85rem; }

                /* ===== STAT CARD MODAL ===== */
                .stat-card-charte {
                    background: white; border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    padding: 1rem; text-align: center;
                    transition: all 0.25s ease;
                }
                .stat-card-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                    border-color: var(--secondary);
                }
                .stat-icon-charte {
                    width: 40px; height: 40px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 0.5rem;
                    font-size: 1.2rem;
                }
                .stat-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.3px; margin-bottom: 4px;
                }
                .stat-value-charte {
                    font-size: 0.95rem; font-weight: 700; color: #1e293b;
                }

                /* ===== BADGE STATUT TABLE MODAL ===== */
                .badge-status-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    padding: 0.25rem 0.75rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte {
                    font-size: 0.8rem; color: #64748b;
                }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== DROPDOWN ===== */
                .dropdown-menu-charte {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.12);
                    padding: 0.5rem;
                }
                .dropdown-menu-charte .dropdown-item {
                    border-radius: 8px;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                }
                .dropdown-menu-charte .dropdown-item:hover {
                    background: #f1f5f9;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTE ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}